// a library to wrap and simplify api calls
import { CancelTokenSource } from 'axios'
import qs from 'query-string'
import { domains } from '~/constants/domains'
import URLS from '~/constants/urls'
import { BlockNumberResponse } from '~/queries/blockNumberQueries'
import {
  extensionType,
  ExtensionTypeLabel,
  ExtensionTypeValue,
} from '~/queries/extensionQueries'
import { observe } from '~/services/observability/observe'
import {
  setSpanOutcome,
  TracedFlow,
  traceFlow,
} from '~/services/observability/tracing'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
  MessagingAnalyticsEvents,
  SharedAnalyticsEvents,
} from '~/types/Analytics'
import {
  GetFaxListRequest,
  GetSnippetsRequest,
  GetVoicemailsRequest,
  IdOrderingFilter,
  UnreadVoicemailStatusFilter,
} from '~/types/ApiRequestTypes'
import {
  CallTranscriptInstance,
  FindCallTranscriptResult,
} from '~/types/CallTranscript'
import {
  AuthenticationResultCallback,
  AuthenticationType,
  CreateThreadResponse,
  GetAuthDataCallback,
  LoginByEmailPayload,
  LoginByEmailResponse,
  PostMessageParams,
  RefreshTokenResponse,
  SendMessageResponse,
} from '~/types/MangoApiClientTypes'
import { stringifyQueryParams } from '~/utils/apiQueryUtils'
import { getAppVersion, isElectron } from '~/utils/common'
import { createSwitchboardNamespace } from '~/utils/mangoApiClient/switchboard/createSwitchboardNamespace'
import { createWebhooksNamespace } from '~/utils/mangoApiClient/webhooks/createWebhooksNamespace'

import {
  createTransportAdapter,
  setAuthInterceptors,
  setRateLimitInterceptors,
} from '@mangovoice/api-client'
import type {
  HttpTransport,
  TransportRequestConfig,
} from '@mangovoice/api-client'
import apisauce, {
  ApiResponse,
  ApisauceInstance,
  CancelToken,
} from '@mangovoice/apisauce'
import type {
  PaginatedQueuesResponse,
  QueueAgentsResponse,
  QueueCallsResponse,
  QueueMetricsApiResponse,
} from '@mangovoice/reporting-ui'
import {
  ApiError,
  CallVolumeMetrics,
  CallVolumeMetricsRequest,
  CallVolumeTimeseriesRequest,
} from '@mangovoice/types'
import { CallsReport, CallsReportRequest } from '@mangovoice/types'
import { AnswerRateMetrics, AnswerRateMetricsRequest } from '@mangovoice/types'
import {
  AnswerRateTimeseries,
  AnswerRateTimeseriesRequest,
} from '@mangovoice/types'
import { AnswerRateRequest, AnswerRateResponse } from '@mangovoice/types'
import {
  NumberComparisonRequest,
  NumberComparisonResponse,
} from '@mangovoice/types'
import {
  AgentStatus,
  AppSessionJwts,
  BlockedNumber,
  BlockNumberRequest,
  Call,
  CallLog,
  CallsQueryParams,
  ClientError,
  Contact,
  ContactSource,
  ContactSourcesQueryParams,
  ContactsQueryParams,
  Extension,
  ExtensionInstance,
  ExtensionPreview,
  Fax,
  FaxInstance,
  Message,
  MessageSnippet,
  MessageSnippetPostResponse,
  MessageThread,
  PaginatedApiResult,
  Pbx,
  PhoneNumber,
  User,
  UserRoles,
  UsersQueryParams,
  Voicemail,
  VoicemailInstance,
} from '@mangovoice/types'
import {
  VoicemailRateTimeseries,
  VoicemailRateTimeseriesRequest,
} from '@mangovoice/types'

type MarkFaxAsReadResponse = {
  message: string
  read_at: string // ISO timestamp
}

export type SecureFaxStatus =
  | 'valid'
  | 'not_found'
  | 'expired'
  | 'disabled'
  | 'max_downloads_reached'
  | 'too_many_attempts'

export interface SecureFaxStatusResponse {
  status: SecureFaxStatus
  fax_document?: string
}

export interface SecureFaxDownloadResponse {
  from_number: string
  to_number: string
  received_at: string
  thumbnail_url: string
  download_url: string
  downloads_remaining: number
}

export type SecureFaxErrorCode =
  | 'password_required'
  | 'invalid_password'
  | 'too_many_attempts'
  | 'max_downloads_reached'
  | 'expired'
  | 'disabled'
  | 'not_found'
  | 's3_error'
  | 'throttled'

export interface SecureFaxErrorResponse {
  type?: string
  code?: SecureFaxErrorCode
  detail?: string
  message?: string
  attempts_remaining?: number
}

let getAuthData: GetAuthDataCallback | undefined

const withJwt = (api: ApisauceInstance) => {
  const { accessToken: jwt } = getAuthData?.() ?? {}
  api.setHeader('Authorization', `Bearer ${jwt ?? ''}`)
  return api
}

// Long enough to absorb a poll storm, short enough that a 401 from a brief
// auth-backend fault doesn't pin a valid token as dead.
const REJECTED_REFRESH_TTL_MS = 5 * 60 * 1000

const create = () => {
  const authApi = apisauce.create({
    baseURL: domains.MANGO_API(),
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    timeout: 10 * 1000, // 10 sec.
  })

  const legacyApi = apisauce.create({
    baseURL: domains.JWT_AUTH(),
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    timeout: 10 * 1000, // 10 sec.
  })

  const mangoApi = apisauce.create({
    baseURL: domains.MANGO_API(),
    headers: {
      Accept: 'application/json; indent=4',
      'Content-Type': 'application/json',
    },
    timeout: 10 * 1000, // 10 sec.
  })

  // Let axios/apisauce set the multipart boundary on FormData bodies. The
  // default 'application/json' header sticks otherwise, and multipart
  // uploads (audio tracks, sound clips) fail because the browser never
  // writes a `Content-Type: multipart/form-data; boundary=…` header.
  mangoApi.addRequestTransform((request) => {
    if (request.data instanceof FormData && request.headers) {
      delete request.headers['Content-Type']
    }
  })

  let authenticationResultCallback: AuthenticationResultCallback | undefined

  const loginByEmail = async (payload: LoginByEmailPayload) => {
    // Attributes stay PHI-clean — never the payload (email/password).
    return await traceFlow(TracedFlow.Login, async (span) => {
      const response = await authApi.post<LoginByEmailResponse>(
        URLS.LOGIN,
        payload,
      )
      setSpanOutcome(span, response.ok)
      return response
    })
  }

  // Coalesce concurrent refreshes (401 interceptor, proactive websocket
  // rotation) into one POST so two callers can't both consume the same
  // one-time refresh token.
  let inFlightRefresh: Promise<ApiResponse<AppSessionJwts, ApiError>> | null =
    null

  // Callers get the last 401 for a refresh token without resending it, until
  // the token changes or the rejection expires.
  let rejectedRefresh: {
    refreshToken: string
    response: ApiResponse<AppSessionJwts, ApiError>
    expiresAtMs: number
  } | null = null

  const getRefreshToken = (): Promise<
    ApiResponse<AppSessionJwts, ApiError>
  > => {
    if (inFlightRefresh) {
      return inFlightRefresh
    }

    const { refreshToken } = getAuthData?.() ?? {}
    if (
      refreshToken &&
      rejectedRefresh?.refreshToken === refreshToken &&
      Date.now() < rejectedRefresh.expiresAtMs
    ) {
      return Promise.resolve(rejectedRefresh.response)
    }

    inFlightRefresh = (async (): Promise<
      ApiResponse<AppSessionJwts, ApiError>
    > => {
      try {
        // Inside the coalescing wrapper, so one span per actual POST rather
        // than one per caller waiting on the shared in-flight promise.
        const refreshResult = await traceFlow(
          TracedFlow.TokenRefresh,
          async (span) => {
            const response = await authApi.post<RefreshTokenResponse>(
              URLS.REFRESH_TOKEN,
              {
                refresh: refreshToken,
              },
            )
            setSpanOutcome(span, response.ok)
            return response
          },
        )

        const appSessionJwt: AppSessionJwts = {
          access_token: refreshResult.data?.access ?? '',
          refresh_token: refreshResult.data?.refresh ?? '',
        }

        // Failure-only telemetry, same policy as the SIP analytics: refreshes
        // succeed hourly per client, so only the failure carries signal.
        if (!refreshResult.ok) {
          observe.track(
            AnalyticsNamespace.AUTHENTICATION,
            AuthenticationAnalyticsEvents.TokenRefreshFailed,
            {
              reasonCode: refreshResult.problem ?? 'unknown',
              status: refreshResult.status,
            },
          )
        }

        if (refreshResult.ok) {
          authenticationResultCallback?.({
            authenticationType: AuthenticationType.RefreshAppSessionJwts,
            ok: true,
            result: appSessionJwt,
          })
        }

        if (refreshResult.status === 401 && refreshToken) {
          // Only the fields callers read; the full response holds the request
          // config, which carries the refresh token.
          const { ok, problem, status, data } = refreshResult
          rejectedRefresh = {
            refreshToken,
            response: { ok, problem, status, data } as ApiResponse<
              AppSessionJwts,
              ApiError
            >,
            expiresAtMs: Date.now() + REJECTED_REFRESH_TTL_MS,
          }
        }
        return refreshResult as ApiResponse<AppSessionJwts, ApiError>
      } finally {
        inFlightRefresh = null
      }
    })()

    return inFlightRefresh
  }

  const setDefaultVersionHeaders = (apiSauceInstances: ApisauceInstance[]) => {
    const doGetAppVersion = async (): Promise<string> => {
      return await getAppVersion()
    }

    doGetAppVersion().then((version: string) => {
      apiSauceInstances.forEach((apiSauceInst) => {
        apiSauceInst.setHeader('x-mango-app', isElectron ? 'desktop' : 'web')
        apiSauceInst.setHeader('x-mango-app-version', version)
      })
    })
  }

  //interceptors run in reverse order - don't change the order of these registrations
  setAuthInterceptors([legacyApi, mangoApi], {
    getAuthData: () => getAuthData?.(),
    refreshToken: getRefreshToken,
    onAuthFailure: (info: { type: string; error?: unknown }) => {
      if (info.type === 'invalid_authentication') {
        authenticationResultCallback?.({
          authenticationType: AuthenticationType.InvalidAuthentication,
          ok: false,
          result: info.error as ApiError,
        })
      } else {
        authenticationResultCallback?.({
          authenticationType: AuthenticationType.RefreshAppSessionJwts,
          ok: false,
          result: info.error as ApiError,
        })
      }
    },
    onTokenRefreshed: () => {
      // Token refresh result is handled inside getRefreshToken via the callback
    },
  })

  const rateLimitReporting = setRateLimitInterceptors(
    [legacyApi, mangoApi, authApi],
    {
      onRateLimited: (info) => {
        observe.track(
          AnalyticsNamespace.SHARED,
          SharedAnalyticsEvents.RateLimitError,
          {
            route: info.url,
            // Sum, don't count: one event can stand for many 429s.
            count: info.count,
          },
        )
      },
    },
  )
  // Reports suppressed inside the coalescing window survive a mid-window
  // reload or quit.
  window.addEventListener('pagehide', () => rateLimitReporting?.flush())
  setDefaultVersionHeaders([authApi, legacyApi, mangoApi])

  const setCallbacks = (
    getAuthDataCallback: GetAuthDataCallback,
    onAuthenticationResult: AuthenticationResultCallback,
  ) => {
    getAuthData = getAuthDataCallback
    authenticationResultCallback = onAuthenticationResult
  }

  // #region  Call History
  // Replaced with Local Call History
  const getCallList = () => withJwt(legacyApi).get(URLS.CALL_LIST)

  const getCalls = (params: CallsQueryParams, signal?: AbortSignal) => {
    const queryString = stringifyQueryParams(params)
    return withJwt(mangoApi).get<PaginatedApiResult<Call>, ApiError>(
      `${URLS.CALLS}?${queryString}`,
      undefined,
      { signal },
    )
  }

  const getCall = (callId: number, signal?: AbortSignal) =>
    withJwt(mangoApi).get<Call, ApiError>(
      `${URLS.CALLS}${callId}/`,
      undefined,
      { signal },
    )

  const getCallByLegUuid = (callLegUuid: string, signal?: AbortSignal) =>
    withJwt(mangoApi).get<Call, ApiError>(
      `/app/v1/calls/${callLegUuid}/`,
      undefined,
      { signal },
    )

  // Missed Calls - try using pbx_ids (plural) like /calls/ endpoint
  const getMissedCalls = (params: CallsQueryParams, signal?: AbortSignal) => {
    // Extract parameters that are not supported by missed calls endpoint
    const { ...restParams } = params

    // Pass through params as-is, including pbx_ids
    const missedCallsParams = {
      ...restParams,
      // Keep pbx_ids, started_after, started_before as-is
    }

    const queryString = stringifyQueryParams(missedCallsParams)
    return withJwt(mangoApi).get<PaginatedApiResult<Call>, ApiError>(
      `${URLS.MISSED_CALLS}?${queryString}`,
      undefined,
      { signal },
    )
  }

  const updateMissedCall = (payload: {
    call_id: number
    follow_up_status: string
    follow_up_user: number | null
    follow_up_time_utc: string
    pbx: number
  }) => {
    return withJwt(mangoApi).post<Call, ApiError>(URLS.MISSED_CALLS, payload)
  }

  const getRecentCalls = (pbxId: string | number) =>
    withJwt(mangoApi).get<PaginatedApiResult<CallLog>, ApiError>(
      URLS.RECENT_CALLS,
      {
        pbx_id: pbxId,
        limit: 100,
      },
    )
  const getCall_Legacy = (callId: number) =>
    withJwt(mangoApi).get(`${URLS.RECENT_CALLS}${callId}/`)

  const startCallsExport = (body: {
    startDate: string
    endDate: string
    pbxIds: number[]
    direction?: 'inbound' | 'outbound'
    answered?: boolean
    hasVoicemail?: boolean
  }) =>
    withJwt(mangoApi).post('/app/v1/reporting/calls/export/', { filters: body })

  const getCallsExportStatus = (jobId: string) =>
    withJwt(mangoApi).get(`/app/v1/reporting/calls/export/status/`, {
      job_id: jobId,
    })
  // #endregion

  // #region  Call Transcripts
  const getTranscribedCallList = (pbxId: number) => {
    const params: CallsQueryParams = {
      pbx_ids: [pbxId],
      has_transcript: true,
      ordering: ['-started_at'],
      limit: 250,
    }
    const queryString = stringifyQueryParams(params)
    const route = `${URLS.CALLS}?${queryString}`

    return withJwt(mangoApi).get<PaginatedApiResult<Call>, ApiError>(route)
  }

  const findCallTranscriptByCallId = (pbxId: number, callId: number) => {
    const queryParams = {
      pbx_id: pbxId,
      call_id: callId,
    }
    const queryString = qs.stringify(queryParams)
    const route = `${URLS.CALL_TRANSCRIPTS}?${queryString}`

    return withJwt(mangoApi).get<
      PaginatedApiResult<FindCallTranscriptResult>,
      ApiError
    >(route)
  }

  const getCallTranscript = (transcriptId: number) => {
    return withJwt(mangoApi).get<CallTranscriptInstance>(
      `${URLS.CALL_TRANSCRIPTS}${transcriptId}/`,
    )
  }
  // #endregion

  // #region  Voicemail
  const getVoicemailExtensions = (pbxId: number) => {
    const queryString = qs.stringify({
      pbx_id: pbxId,
    })

    return withJwt(mangoApi).get<PaginatedApiResult<Extension>, ApiError>(
      `${URLS.EXTENSIONS}?${queryString}`,
    )
  }

  const getAllExtensions = (pbxId: number) => {
    const queryParams = {
      pbx_id: pbxId,
      include_voicemail_status: true,
      limit: 1000,
    }

    const queryString = qs.stringify(queryParams)

    return withJwt(mangoApi).get<PaginatedApiResult<Extension>, ApiError>(
      `${URLS.EXTENSIONS_SELECT}?${queryString}`,
    )
  }

  const moveVoicemail = async (
    voicemailId: number,
    extensionId: number,
  ): Promise<{ message: string }> => {
    const response: ApiResponse<{ message: string }, ApiError> = await withJwt(
      mangoApi,
    ).put(`${URLS.VOICEMAILS}${voicemailId}/move-to-extension/`, {
      extension_id: extensionId,
    })

    if (!response.ok) {
      const errorData = response.data
      console.error('API Error Data:', errorData)
      const error: ApiError = {
        type: 'ApiError',
        status: response.status,
        code: errorData?.code,
        message: errorData?.message || 'Failed to move voicemail',
        parameters: errorData?.parameters,
      }
      throw error
    }
    return response.data as { message: string }
  }

  const getVoicemails = ({
    pbxId,
    extensionId,
    search,
    offset,
    limit = 25,
    unread = UnreadVoicemailStatusFilter.ALL,
  }: GetVoicemailsRequest) => {
    const queryString = qs.stringify({
      ordering: '-id',
      pbx_id: pbxId,
      extension_id: extensionId,
      search,
      limit,
      unread,
      ...(offset && { offset }),
    })

    return withJwt(mangoApi).get<PaginatedApiResult<Voicemail>, ApiError>(
      `${URLS.VOICEMAILS}?${queryString}`,
    )
  }
  const getVoicemail = (voicemailId: number) =>
    withJwt(mangoApi).get<VoicemailInstance, ApiError>(
      `${URLS.VOICEMAILS}${voicemailId}/`,
    )
  const markVoicemailRead = (voicemailId: number) =>
    withJwt(mangoApi).put<{ message: string }, ApiError>(
      `${URLS.VOICEMAILS}${voicemailId}/mark-as-read/`,
    )
  const markVoicemailUnread = (voicemailId: number) =>
    withJwt(mangoApi).put<{ message: string }, ApiError>(
      `${URLS.VOICEMAILS}${voicemailId}/mark-as-unread/`,
    )
  const deleteVoicemail = (voicemailId: number) =>
    withJwt(mangoApi).delete<{ message: string }, ApiError>(
      `${URLS.VOICEMAILS}${voicemailId}/`,
    )
  // #endregion

  // #region  Faxing
  const sendFax = ({
    formData,
    onUploadProgress,
    onRequestStarted,
  }: {
    formData: FormData
    onUploadProgress?: (e: ProgressEvent) => void
    onRequestStarted?: (token: CancelTokenSource) => void
  }) => {
    const source = CancelToken.source()
    onRequestStarted?.(source)
    const sendingFaxApi = withJwt(mangoApi).post(URLS.SEND_FAX, formData, {
      headers: {
        'Content-Type': `multipart/form-data;`,
      },
      onUploadProgress: (progressEvent) =>
        // eslint-disable-next-line no-restricted-syntax -- grandfathered type escape hatch, remove with the API-types burn-down (see PR #1763)
        onUploadProgress?.(progressEvent as unknown as ProgressEvent),
      cancelToken: source.token,
      timeout: 60 * 1000,
    })
    return sendingFaxApi
  }

  const resendFax = ({ faxId }: { faxId: number }) => {
    const uri = `${URLS.FAXES}${faxId}/resend/`
    return withJwt(mangoApi).put<unknown, ApiError>(uri)
  }

  const getFaxList = (
    {
      pbxId,
      offset,
      search,
      direction,
      extension_id,
      unread,
      limit = 50,
      ordering = IdOrderingFilter.DESCENDING,
    }: GetFaxListRequest,
    signal?: AbortSignal,
  ) => {
    const queryString = qs.stringify({
      ordering,
      pbx_id: pbxId,
      offset,
      search,
      extension_id,
      unread,
      limit,
      ...(direction && { direction }), // This syntax cleans up the network logs to avoid creating blank query params
    })

    return withJwt(mangoApi).get<PaginatedApiResult<Fax>, ApiError>(
      `${URLS.FAXES}?${queryString}`,
      {
        signal,
      },
    )
  }
  const getFax = (faxId: number, signal?: AbortSignal) =>
    withJwt(mangoApi).get<FaxInstance, ApiError>(
      `${URLS.FAXES}${faxId}/`,
      undefined,
      { signal },
    )

  const deleteFax = (faxId: number) => {
    return withJwt(mangoApi).delete(`${URLS.FAXES}${faxId}/`)
  }

  // /app/v1/faxes/{id}/move-to-virtual-fax/
  interface MoveFaxToVirtualFaxResponse {
    extension_id: number
  }
  const moveFaxToVirtualFax = (
    faxId: number,
    body: { extension_id: number },
  ) => {
    return withJwt(mangoApi).put<MoveFaxToVirtualFaxResponse, ApiError>(
      `${URLS.FAXES}${faxId}/move-to-virtual-fax/`,
      body,
    )
  }

  // /app/v1/faxes/{id}/mark-as-unread/
  interface MarkFaxAsUnreadResponse {
    message: string
  }
  const markFaxAsUnread = (faxId: number) => {
    return withJwt(mangoApi).put<
      MarkFaxAsUnreadResponse,
      ApiError | ClientError
    >(`${URLS.FAXES}${faxId}/mark-as-unread/`)
  }

  // /app/v1/faxes/{id}/mark-as-read/
  const markFaxAsRead = (faxId: number) => {
    return withJwt(mangoApi).put<MarkFaxAsReadResponse, ApiError | ClientError>(
      `${URLS.FAXES}${faxId}/mark-as-read/`,
    )
  }

  const getSecureFaxStatus = async (token: string) => {
    return await authApi.get<SecureFaxStatusResponse, SecureFaxErrorResponse>(
      `/v1/secure-faxes/${encodeURIComponent(token)}/`,
    )
  }

  const downloadSecureFax = async (token: string, password: string) => {
    return await authApi.post<
      SecureFaxDownloadResponse,
      SecureFaxErrorResponse
    >(`/v1/secure-faxes/${encodeURIComponent(token)}/download/`, { password })
  }
  // #endregion

  // #region  Snippets
  const getSnippets = ({
    pbxId,
    offset,
    personal = false,
  }: GetSnippetsRequest) => {
    const resolvedPbxId = personal ? '' : (pbxId ?? '')
    const scope = personal ? 'user' : ''
    const queryString = qs.stringify({
      ordering: '-id',
      pbx_id: resolvedPbxId,
      scope,
      ...(offset && { offset }),
    })

    return withJwt(mangoApi).get<PaginatedApiResult<MessageSnippet>, ApiError>(
      `${URLS.GET_SNIPPETS}?${queryString}`,
    )
  }

  const searchSnippets = (search: string) => {
    const queryString = qs.stringify({
      ordering: '-id',
      limit: 500,
      ...(search && { search }),
    })

    return withJwt(mangoApi).get<PaginatedApiResult<MessageSnippet>, ApiError>(
      `${URLS.GET_SNIPPETS}?${queryString}`,
    )
  }

  const postSnippet = (payload: Partial<MessageSnippet> & { id?: number }) => {
    const { id } = payload
    if (id)
      return withJwt(mangoApi).put<MessageSnippetPostResponse, ApiError>(
        `${URLS.GET_SNIPPETS}${id}/`,
        {
          ...payload,
        },
      )
    return withJwt(mangoApi).post<MessageSnippetPostResponse, ApiError>(
      URLS.GET_SNIPPETS,
      { ...payload },
    )
  }
  const deleteSnippet = (snippetId: number) =>
    withJwt(mangoApi).delete<undefined, ApiError>(
      `${URLS.GET_SNIPPETS}${snippetId}/`,
    )

  // #endregion

  // #region  Numbers
  const getPhoneNumber = ({ phoneNumberId }: { phoneNumberId: number }) => {
    return withJwt(mangoApi).get(`${URLS.PHONE_NUMBERS}${phoneNumberId}/`)
  }
  const getPhoneNumbers = ({
    pbx_id,
    offset = 0,
    limit = 25,
    search = '',
  }: {
    pbx_id: number | undefined
    offset?: number
    limit?: number
    search?: string
  }) => {
    const queryString = qs.stringify({
      pbx_id,
      offset,
      limit,
      ...(search && { search }),
    })
    return withJwt(mangoApi).get<PaginatedApiResult<PhoneNumber>, ApiError>(
      `${URLS.PHONE_NUMBERS}?${queryString}`,
    )
  }
  // #endregion

  // #region PBX
  const getUserPbxList = (params?: {
    limit?: number
    offset?: number
    search?: string
  }) => {
    const queryString = stringifyQueryParams({
      limit: params?.limit ?? 100,
      ...(params?.offset ? { offset: params.offset } : {}),
      ...(params?.search ? { search: params.search } : {}),
    })

    return withJwt(mangoApi).get<PaginatedApiResult<Pbx>, ApiError>(
      `${URLS.GET_PBXES}?${queryString}`,
    )
  }

  const getPbx = (pbxId: number | string) => {
    return withJwt(mangoApi).get<Pbx, ApiError>(`${URLS.GET_PBXES}${pbxId}/`)
  }

  const getPbxUsers = (params?: UsersQueryParams, signal?: AbortSignal) => {
    const queryString = stringifyQueryParams(params ?? {})
    return withJwt(mangoApi).get<PaginatedApiResult<User>, ApiError>(
      `${URLS.GET_PBX_USERS}?${queryString}`,
      undefined,
      { signal },
    )
  }

  const getUserRoles = (userId: number) => {
    return withJwt(mangoApi).get<UserRoles, ApiError>(`/users/${userId}/roles/`)
  }
  // #endregion

  // #region  SIP Connection
  const getSipConnectionInfo = (extensionId: number) => {
    return withJwt(mangoApi).get(
      `${URLS.EXTENSIONS}${extensionId}/sip-connection-info/`,
    )
  }
  // #endregion

  // #region Time Zones
  const getTimeZones = () => {
    return withJwt(mangoApi).get<
      { group: string; label: string; iana_time_zone: string }[]
    >('/time-zones/')
  }
  // #endregion

  // #region Extensions

  const getPbxExtensions = (
    pbxId: string | number,
    type: ExtensionTypeValue = extensionType('Line'),
    count = 999,
  ): Promise<ApiResponse<PaginatedApiResult<ExtensionPreview>, ApiError>> => {
    const queryString = qs.stringify({
      pbx_id: pbxId,
      type: type,
      limit: count,
    })
    return withJwt(mangoApi).get(`${URLS.EXTENSIONS}select/?${queryString}`)
  }

  const getUserExtensions = (pbxId?: number) => {
    const options = qs.stringify({
      pbx_id: pbxId,
      type: extensionType('Line'),
      limit: 999,
      ordering: 'name',
    })

    return withJwt(mangoApi).get<
      PaginatedApiResult<ExtensionInstance>,
      ApiError
    >(`${URLS.EXTENSIONS}?${options}`)
  }

  interface GetExtensionsParams {
    search?: string
    pbx_id?: number
    offset?: number
    limit?: number
    type?: ExtensionTypeLabel
  }
  //get all extensions on login
  const getExtensions = ({
    search,
    pbx_id,
    type = 'Line',
    offset,
    limit,
  }: GetExtensionsParams) => {
    const queryString = qs.stringify({
      offset,
      limit,
      search,
      pbx_id,
      type: extensionType(type),
    })
    return withJwt(mangoApi).get<
      PaginatedApiResult<ExtensionInstance>,
      ApiError
      // We added an extended timeout to accommodate customers with exceptionally high extension counts (statistical tail)
    >(`${URLS.EXTENSIONS}?${queryString}`, undefined, { timeout: 60 * 1000 })
  }

  const getExtensionInfo = (
    extensionId?: number | string,
  ): Promise<ApiResponse<ExtensionInstance, ApiError>> =>
    withJwt(mangoApi).get(`/extensions/${extensionId ? `${extensionId}/` : ''}`)

  // #endregion

  // #region Messaging

  // Message Threads
  const getThreads = ({
    group_id,
    external_phone_number,
  }: {
    group_id?: string | number
    external_phone_number?: string
  }) => {
    return withJwt(mangoApi).get(URLS.MESSAGE_THREADS, {
      message_group_id: group_id,
      external_phone_number,
    })
  }

  const createThread = ({
    groupId,
    externalPhoneNumber,
    name,
  }: {
    groupId: number
    externalPhoneNumber: string
    name?: string
  }) =>
    withJwt(mangoApi).post<CreateThreadResponse, ApiError>(
      URLS.MESSAGE_THREADS,
      {
        group_id: groupId,
        external_phone_number: externalPhoneNumber,
        name,
      },
    )

  // Message Groups
  const getMessageGroups = (pbxId: number | undefined) => {
    const query = qs.stringify({
      pbx_id: pbxId,
    })
    return withJwt(mangoApi).get(`/message-groups/?${query}`)
  }

  const getMessageThread = (threadId: number) => {
    return withJwt(mangoApi).get<MessageThread, ApiError>(
      `/message-threads/${threadId}/`,
    )
  }

  // Message Threads
  const getMessageThreads = ({
    offset,
    search,
    groupId,
    pbxId,
    hasUnreadMessages,
    contactId,
    limit,
  }: {
    offset?: number | string
    search?: string
    groupId?: number | string
    pbxId?: number | string
    hasUnreadMessages?: boolean
    contactId?: number
    limit?: number
  }) => {
    const queryString = qs.stringify({
      ordering: '-last_message,-id',
      limit: limit ?? 25,
      offset: offset ?? 0,
      pbx_id: pbxId ?? undefined,
      group_id: groupId ?? undefined,
      message_group_id: groupId ?? undefined,
      has_unread_messages: hasUnreadMessages ?? undefined,
      search: search ?? undefined,
      contact_id: contactId ?? undefined,
    })

    return withJwt(mangoApi).get<PaginatedApiResult<MessageThread>, ApiError>(
      `/message-threads/?${queryString}`,
    )
  }
  const updateThreadName = ({
    threadId,
    name,
  }: {
    threadId: number
    name: string
  }) => {
    return withJwt(mangoApi).put<MessageThread, ApiError>(
      `/message-threads/${threadId}/`,
      {
        name,
      },
    )
  }

  const deleteThread = (threadId: number) => {
    return withJwt(mangoApi).delete(`/message-threads/${threadId}/`)
  }
  const markMessageThreadAsRead = (threadId: number) => {
    return withJwt(mangoApi).put(`/message-threads/${threadId}/mark-as-read/`)
  }

  const markMessageGroupAsRead = (
    groupId: number,
  ): Promise<ApiResponse<{ message: string }, ApiError>> => {
    return withJwt(mangoApi).put(`/message-groups/${groupId}/mark-as-read/`)
  }

  // Messages
  const getMessages = ({
    threadId,
    offset,
    limit,
  }: {
    threadId: number
    offset?: number
    limit?: number
  }): Promise<ApiResponse<PaginatedApiResult<Message>, ApiError>> => {
    const queryParams = qs.stringify({
      thread_id: threadId,
      ordering: '-id',
      offset: offset ?? 0,
      limit: limit ?? 50,
    })
    return withJwt(mangoApi).get(`${URLS.GET_MESSAGES}?${queryParams}`)
  }

  const postMessage = async ({
    threadId,
    body,
    file,
    onProgress,
  }: PostMessageParams): Promise<
    ApiResponse<SendMessageResponse, ApiError>
  > => {
    const formData = new FormData()
    formData.append('thread_id', threadId.toString())
    if (body) {
      formData.append('body', body)
    }
    if (file) {
      formData.append('file', file)
    }

    const startedAtMs = Date.now()
    // Attributes stay PHI-clean — never the message body or the file.
    const response = await traceFlow(
      TracedFlow.MessageSend,
      async (span) => {
        const sendResponse = await withJwt(mangoApi).post<Message, ApiError>(
          URLS.GET_MESSAGES,
          formData,
          {
            onUploadProgress: (progressEvent) =>
              onProgress?.(progressEvent.progress ?? 0),
            headers: {
              'Content-Type':
                'multipart/form-data;boundary=----WebKitFormBoundaryyrV7KO0BoCBuDbTL',
            },
          },
        )
        setSpanOutcome(span, sendResponse.ok)
        return sendResponse
      },
      { hasAttachment: Boolean(file) },
    )

    // Single choke point for every message-send path. PHI-clean properties.
    observe.track(
      AnalyticsNamespace.MESSAGING,
      response.ok
        ? MessagingAnalyticsEvents.MessageSent
        : MessagingAnalyticsEvents.MessageSendFailed,
      {
        durationMs: Date.now() - startedAtMs,
        hasAttachment: Boolean(file),
        ...(response.ok
          ? {}
          : {
              reasonCode: response.problem ?? 'unknown',
              status: response.status,
            }),
      },
    )

    return response
  }

  // #endregion

  // #region Contacts
  const getContactSources = (
    queryParams: ContactSourcesQueryParams = {
      limit: 25,
      offset: 0,
    },
  ) => {
    return withJwt(mangoApi).get<PaginatedApiResult<ContactSource>, ApiError>(
      `${URLS.CONTACT_SOURCES}?${qs.stringify(queryParams)}`,
    )
  }

  const getContactSource = (contactSourceId: number) => {
    return withJwt(mangoApi).get<ContactSource, ApiError>(
      `${URLS.CONTACT_SOURCES}${contactSourceId}/`,
    )
  }

  const getContacts = (
    queryParams: ContactsQueryParams,
    signal?: AbortSignal,
  ) => {
    return withJwt(mangoApi).get<PaginatedApiResult<Contact>, ApiError>(
      `${URLS.CONTACTS}?${qs.stringify(queryParams)}`,
      undefined,
      { signal },
    )
  }

  const getContact = (contactId: number | string, pbxId: number) => {
    return withJwt(mangoApi).get<Contact, ApiError>(
      `${URLS.CONTACTS}${contactId}/?${qs.stringify({ pbx_id: pbxId })}`,
    )
  }
  // #endregion

  // #region Blocked Numbers
  const getBlockedNumbers = (pbxId: number) => {
    return withJwt(mangoApi).get<PaginatedApiResult<BlockedNumber>, ApiError>(
      `${URLS.BLOCKED_NUMBERS}?${qs.stringify({ pbx_id: pbxId, limit: 9999 })}`,
    )
  }

  const blockNumber = (pbxId: number, phoneNumber: string) => {
    const request: BlockNumberRequest = {
      pbx_id: pbxId,
      phone_number: phoneNumber,
    }
    return withJwt(mangoApi).post<BlockNumberResponse>(
      `${URLS.BLOCKED_NUMBERS}`,
      request,
    )
  }

  const unblockNumber = (blockedNumberId: number) => {
    return withJwt(mangoApi).delete(
      `${URLS.BLOCKED_NUMBERS}${blockedNumberId}/`,
    )
  }

  // #endregion

  // #region Call Queues
  const getQueueAgentStatus = (extensionId: number) => {
    return withJwt(mangoApi).get<AgentStatus, ApiError>(
      `${URLS.QUEUE_AGENT_STATUS}`.replace(
        '{extensionId}',
        extensionId.toString(),
      ),
    )
  }

  const putQueueLogin = (extensionId: number) => {
    return withJwt(mangoApi).put<undefined, ApiError>(
      `${URLS.QUEUE_LOG_IN}`.replace('{extensionId}', extensionId.toString()),
    )
  }

  const putQueueLogout = (extensionId: number) => {
    return withJwt(mangoApi).put<undefined, ApiError>(
      `${URLS.QUEUE_LOG_OUT}`.replace('{extensionId}', extensionId.toString()),
    )
  }

  // #endregion

  //#region Queue Dashboard
  const getQueueDashboardQueues = (pbxId: number, signal?: AbortSignal) => {
    return withJwt(mangoApi).get<PaginatedQueuesResponse, ApiError>(
      `/app/v1/extensions/`,
      { pbx_id: pbxId, type: 'ExtensionCallQueue' },
      { signal },
    )
  }

  const getQueueDashboardCalls = (
    pbxUuid: string,
    queueNumber: string,
    signal?: AbortSignal,
  ) => {
    return withJwt(mangoApi).get<QueueCallsResponse, ApiError>(
      `/app/v1/call-center-queues/members/`,
      { pbx_uuid: pbxUuid, number: queueNumber },
      { signal },
    )
  }

  const getQueueDashboardAgents = (
    pbxUuid: string,
    queueNumber: string,
    signal?: AbortSignal,
  ) => {
    return withJwt(mangoApi).get<QueueAgentsResponse, ApiError>(
      `/app/v1/call-center-queues/agents/`,
      { pbx_uuid: pbxUuid, number: queueNumber },
      { signal },
    )
  }

  const getQueueMetrics = (
    params: { start: string; end: string; pbxes: string; queue: string },
    signal?: AbortSignal,
  ) => {
    const qs = new URLSearchParams(params)
    return withJwt(mangoApi).get<QueueMetricsApiResponse, ApiError>(
      `/app/v1/reporting/queue/summary/?${qs.toString()}`,
      undefined,
      { signal },
    )
  }

  const postQueueDashboardAgentLogin = (agentId: string) => {
    return withJwt(mangoApi).post<{ success: boolean }, ApiError>(
      `/api/agents/${agentId}/login`,
    )
  }

  const postQueueDashboardAgentLogout = (agentId: string) => {
    return withJwt(mangoApi).post<{ success: boolean }, ApiError>(
      `/api/agents/${agentId}/logout`,
    )
  }
  //#endregion

  //#region Reporting
  /**
   * Sends a calls report request to the Mango API and returns a calls report.
   * This endpoint expects local timezone values to be included in the request.
   *
   * @param reportRequest - The payload to build the report.
   * @param signal - Optional AbortSignal to cancel the request if necessary.
   * @returns A promise resolving to the ApiResponse containing the CallsReport or an ApiError.
   */
  const getCallsReport = (
    reportRequest: CallsReportRequest,
    signal?: AbortSignal,
  ) => {
    // No changes needed - the local timezone is already included in the request
    return withJwt(mangoApi).post<CallsReport, ApiError>(
      URLS.REPORTING_CALLS,
      reportRequest,
      { signal },
    )
  }

  /**
   * Fetches answer rate metrics from the API.
   * This endpoint requires UTC timestamps with a tz=UTC parameter.
   *
   * @param request - The request parameters including dates and PBX IDs
   * @param signal - Optional AbortSignal to cancel the request if necessary
   * @returns A promise resolving to the ApiResponse containing the AnswerRateMetrics or an ApiError
   */
  const getAnswerRateMetrics = (
    request: AnswerRateMetricsRequest,
    signal?: AbortSignal,
  ) => {
    const { startDate, endDate, pbxIds, phoneNumbers } = request

    // URL encode the timestamps to handle special characters like ":" and "+"
    const encodedStartDate = encodeURIComponent(startDate)
    const encodedEndDate = encodeURIComponent(endDate)

    // Build URL with parameters in specified order: start, end, pbxes, tz=UTC, phones
    // Always using UTC timezone for this specific endpoint
    let url = `/app/v1/reporting/metrics/answer-rate/?start=${encodedStartDate}&end=${encodedEndDate}&pbxes=${pbxIds.join(',')}`

    // Add phones parameter only if it exists
    if (phoneNumbers && phoneNumbers.length > 0) {
      url += `&phone_number=${phoneNumbers.join(',')}`
    }

    return withJwt(mangoApi).get<AnswerRateMetrics, ApiError>(url, undefined, {
      signal,
    })
  }

  /**
   * Fetches answer rate timeseries data from the API.
   *
   * @param request - The request parameters including dates, PBX IDs and optional phone numbers
   * @param signal - Optional AbortSignal to cancel the request if necessary
   * @returns A promise resolving to the ApiResponse containing the AnswerRateTimeseries or an ApiError
   */
  const getAnswerRateTimeseries = (
    request: AnswerRateTimeseriesRequest,
    signal?: AbortSignal,
  ) => {
    const { startDate, endDate, pbxIds, phoneNumbers, timezone, granularity } =
      request

    // URL encode the timestamps to handle special characters like ":" and "+"
    // Timestamps should already include timezone offset in RFC3339 format
    const encodedStartDate = encodeURIComponent(startDate)
    const encodedEndDate = encodeURIComponent(endDate)

    // For now, we're only sending a single PBX ID as requested
    const pbxId = pbxIds.length > 0 ? pbxIds[0] : 0

    // Build URL with parameters in specified order: start, end, pbxes
    let url = `/app/v1/reporting/timeseries/answer-rate/?start=${encodedStartDate}&end=${encodedEndDate}&pbxes=${pbxId}`

    // Add phone_numbers parameter only if it exists
    if (phoneNumbers && phoneNumbers.length > 0) {
      url += `&phone_number=${phoneNumbers.join(',')}`
    }

    // Add timezone parameter if it exists
    if (timezone) {
      url += `&tz=${encodeURIComponent(timezone)}`
    }

    // Include granularity parameter only when specified (for heatmaps)
    if (granularity) {
      url += `&granularity=${granularity}`
    }

    return withJwt(mangoApi).get<AnswerRateTimeseries, ApiError>(
      url,
      undefined,
      {
        signal,
      },
    )
  }

  /**
   * Fetches **aggregate call-volume metrics** from the API.
   *
   * Parameters and behaviour mirror `getAnswerRateMetrics`.
   * External route: `/app/v1/reporting/metrics/call-volume/`
   */
  const getCallVolumeMetrics = (
    request: CallVolumeMetricsRequest,
    signal?: AbortSignal,
  ) => {
    const { startDate, endDate, pbxIds, phoneNumbers } = request

    const encodedStartDate = encodeURIComponent(startDate)
    const encodedEndDate = encodeURIComponent(endDate)

    let url =
      `/app/v1/reporting/metrics/call-volume/?start=${encodedStartDate}` +
      `&end=${encodedEndDate}&pbxes=${pbxIds.join(',')}`

    if (phoneNumbers && phoneNumbers.length > 0) {
      url += `&phone_number=${phoneNumbers.join(',')}`
    }

    return withJwt(mangoApi).get<CallVolumeMetrics, ApiError>(url, undefined, {
      signal,
    })
  }

  /**
   * Fetches **hourly call-volume timeseries** data from the API.
   *
   * Parameters and behaviour mirror `getAnswerRateTimeseries`.
   * External route: `/app/v1/reporting/timeseries/call-volume/`
   */
  const getCallVolumeTimeseries = (
    request: CallVolumeTimeseriesRequest,
    signal?: AbortSignal,
  ) => {
    const { startDate, endDate, pbxIds, phoneNumbers, timezone, granularity } =
      request

    const encodedStartDate = encodeURIComponent(startDate)
    const encodedEndDate = encodeURIComponent(endDate)

    const pbxId = pbxIds.length > 0 ? pbxIds[0] : 0

    let url =
      `/app/v1/reporting/timeseries/call-volume/?start=${encodedStartDate}` +
      `&end=${encodedEndDate}&pbxes=${pbxId}`

    if (phoneNumbers && phoneNumbers.length > 0) {
      url += `&phone_number=${phoneNumbers.join(',')}`
    }

    if (timezone) {
      url += `&tz=${encodeURIComponent(timezone)}`
    }

    // Include granularity parameter only when specified (for heatmaps)
    if (granularity) {
      url += `&granularity=${granularity}`
    }

    return withJwt(mangoApi).get<AnswerRateTimeseries, ApiError>(
      url,
      undefined,
      {
        signal,
      },
    )
  }

  /**
   * Fetches **voicemail rate timeseries** data from the API.
   *
   * Parameters and behaviour mirror other timeseries endpoints.
   * External route: `/app/v1/timeseries/voicemail-rate/`
   */
  const getVoicemailRateTimeseries = (
    request: VoicemailRateTimeseriesRequest,
    signal?: AbortSignal,
  ) => {
    const { startDate, endDate, pbxIds, phoneNumbers, timezone } = request

    const encodedStartDate = encodeURIComponent(startDate)
    const encodedEndDate = encodeURIComponent(endDate)

    const pbxId = pbxIds.length > 0 ? pbxIds[0] : 0

    let url =
      `/app/v1/reporting/timeseries/voicemail-rate/?start=${encodedStartDate}` +
      `&end=${encodedEndDate}&pbxes=${pbxId}`

    if (phoneNumbers && phoneNumbers.length > 0) {
      url += `&phone_number=${phoneNumbers.join(',')}`
    }

    if (timezone) {
      url += `&tz=${encodeURIComponent(timezone)}`
    }

    return withJwt(mangoApi).get<VoicemailRateTimeseries, ApiError>(
      url,
      undefined,
      {
        signal,
      },
    )
  }

  /**
   * Fetches answer rate data from the NEW Answer Rate API endpoint.
   * This is a new endpoint that provides flexible grouping options.
   *
   * External route: `/app/v1/reporting/answer-rate/`
   *
   * @param request - The request parameters
   * @param signal - Optional AbortSignal to cancel the request if necessary
   * @returns A promise resolving to the ApiResponse containing the AnswerRateResponse or an ApiError
   *
   * @example
   * // Get aggregated answer rate for a date range
   * const response = await getAnswerRate({
   *   start: "2024-01-01T00:00:00Z",
   *   end: "2024-01-31T23:59:59Z",
   *   pbx: "1,2,3"
   * });
   *
   * @example
   * // Get answer rate grouped by day
   * const response = await getAnswerRate({
   *   start: "2024-01-01T00:00:00Z",
   *   end: "2024-01-31T23:59:59Z",
   *   pbx: "1,2,3",
   *   group_by: "day",
   *   tz: "America/New_York"
   * });
   */
  const getAnswerRate = (request: AnswerRateRequest, signal?: AbortSignal) => {
    // Convert phone_number array to comma-separated string
    const phoneNumber = request.phone_number
      ? Array.isArray(request.phone_number)
        ? request.phone_number.join(',')
        : request.phone_number
      : undefined

    const queryString = qs.stringify(
      {
        start: request.start,
        end: request.end,
        pbx: request.pbx,
        ...(phoneNumber && { phone_number: phoneNumber }),
        ...(request.tz && { tz: request.tz }),
        ...(request.group_by && { group_by: request.group_by }),
      },
      { arrayFormat: 'none' },
    )

    return withJwt(mangoApi).get<AnswerRateResponse, ApiError>(
      `/app/v1/reporting/answer-rate/?${queryString}`,
      undefined,
      { signal },
    )
  }

  /**
   * Fetches number comparison data from the Number Comparison API.
   * Provides call statistics grouped by phone number.
   *
   * @param request - Request parameters for the number comparison API
   * @param signal - Optional AbortSignal for cancellation
   * @returns Response with number comparison segments
   *
   * @example
   * // Get number comparison data grouped by phone number
   * const response = await getNumberComparison({
   *   start: "2024-01-01T00:00:00Z",
   *   end: "2024-01-31T23:59:59Z",
   *   pbx: "4",
   *   group_by: "phone_number"
   * });
   */
  const getNumberComparison = (
    request: NumberComparisonRequest,
    signal?: AbortSignal,
  ) => {
    // Convert phone_number array to comma-separated string
    const phoneNumber = request.phone_number
      ? Array.isArray(request.phone_number)
        ? request.phone_number.join(',')
        : request.phone_number
      : undefined

    const queryString = qs.stringify(
      {
        start: request.start,
        end: request.end,
        pbx: request.pbx,
        ...(phoneNumber && { phone_number: phoneNumber }),
        ...(request.tz && { tz: request.tz }),
        ...(request.group_by && { group_by: request.group_by }),
      },
      { arrayFormat: 'none' },
    )

    return withJwt(mangoApi).get<NumberComparisonResponse, ApiError>(
      `/app/v1/reporting/number-comparison/?${queryString}`,
      undefined,
      { signal },
    )
  }
  //#endregion

  /**
   * Attempts to make a small api request to verify the user is able to access the api, is authenticated, and if not will attempt to refresh the token.
   * @returns true if successful, false if not
   */

  const switchboard = createSwitchboardNamespace({ withJwt, mangoApi })
  const webhooks = createWebhooksNamespace({ withJwt, mangoApi })

  /**
   * JWT-authenticated HTTP helpers for arbitrary `/app/v1/...` (and similar) paths on MANGO_API.
   * Same implementation as `switchboard.request`; prefer this name outside switchboard-ui adapters.
   */
  const appRequest = switchboard.request

  /** Low-level GET for app URLs (e.g. profile). Use for adapters that need raw request/response. */
  const requestGet = <T = unknown>(
    url: string,
    params?: Record<string, unknown>,
  ) => withJwt(mangoApi).get<T, ApiError>(url, params as Record<string, string>)
  /** Low-level PATCH for app URLs (e.g. profile update). Use for adapters that need raw request/response. */
  const requestPatch = <T = unknown>(
    url: string,
    data?: Record<string, unknown>,
  ) => withJwt(mangoApi).patch<T, ApiError>(url, data ?? {})

  const transport: HttpTransport = createTransportAdapter(mangoApi)

  const getHttpTransport = (): HttpTransport => {
    const authedTransport: HttpTransport = {
      get<T>(
        url: string,
        params?: Record<string, unknown>,
        config?: TransportRequestConfig,
      ) {
        withJwt(mangoApi)
        return transport.get<T>(url, params, config)
      },
      post<T>(url: string, data?: unknown, config?: TransportRequestConfig) {
        withJwt(mangoApi)
        return transport.post<T>(url, data, config)
      },
      put<T>(url: string, data?: unknown, config?: TransportRequestConfig) {
        withJwt(mangoApi)
        return transport.put<T>(url, data, config)
      },
      patch<T>(url: string, data?: unknown, config?: TransportRequestConfig) {
        withJwt(mangoApi)
        return transport.patch<T>(url, data, config)
      },
      delete<T>(
        url: string,
        params?: Record<string, unknown>,
        config?: TransportRequestConfig,
      ) {
        withJwt(mangoApi)
        return transport.delete<T>(url, params, config)
      },
    }
    return authedTransport
  }

  return {
    requestGet,
    requestPatch,
    // Authentication
    setCallbacks,
    loginByEmail,
    getRefreshToken,
    switchboard,
    appRequest,
    webhooks,
    transport,
    getHttpTransport,

    // Voicemails
    getVoicemailExtensions,
    getVoicemails,
    getVoicemail,
    markVoicemailRead,
    markVoicemailUnread,
    deleteVoicemail,

    // Calls / Transcripts
    getCallList,
    getRecentCalls,
    getTranscribedCallList,
    findCallTranscriptByCallId,
    getCalls,
    getCall,
    getCallByLegUuid,
    getMissedCalls,
    updateMissedCall,
    getCallTranscript,
    getCall_Legacy,
    startCallsExport,
    getCallsExportStatus,

    // Faxing
    getFaxList,
    getFax,
    sendFax,
    resendFax,
    moveFaxToVirtualFax,
    deleteFax,
    markFaxAsRead,
    markFaxAsUnread,
    getSecureFaxStatus,
    downloadSecureFax,

    // Extensions
    getExtensions,
    getPbxExtensions,
    getUserExtensions,
    getExtensionInfo,
    moveVoicemail,
    getAllExtensions,

    // Messaging / Threads
    getThreads,
    createThread,
    updateThreadName,
    getMessageGroups,
    getMessageThread,
    getMessageThreads,
    markMessageGroupAsRead,
    markMessageThreadAsRead,
    deleteThread,
    getMessages,
    postMessage,

    // Phone Numbers
    getPhoneNumber,
    getPhoneNumbers,

    // Snippets
    getSnippets,
    searchSnippets,
    postSnippet,
    deleteSnippet,

    // PBX
    getUserPbxList,
    getPbx,
    getPbxUsers,
    getUserRoles,

    // Contacts
    getContactSources,
    getContactSource,
    getContacts,
    getContact,

    // Blocked Numbers
    getBlockedNumbers,
    blockNumber,
    unblockNumber,

    // Call Queues / Agent Statuses
    getQueueAgentStatus,
    putQueueLogin,
    putQueueLogout,

    // Queue Dashboard
    getQueueDashboardQueues,
    getQueueDashboardCalls,
    getQueueDashboardAgents,
    getQueueMetrics,
    postQueueDashboardAgentLogin,
    postQueueDashboardAgentLogout,

    // Dashboards / Reports
    getCallsReport,
    getAnswerRateMetrics,
    getAnswerRateTimeseries,
    getCallVolumeMetrics,
    getCallVolumeTimeseries,
    getVoicemailRateTimeseries,
    getAnswerRate,
    getNumberComparison,

    // Other
    getSipConnectionInfo,
    getTimeZones,
  }
}

export function isClientError(candidate: any): candidate is ClientError {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return false
  }

  const requiredKeys = ['type']
  if (
    requiredKeys.some(
      (key) => !Object.prototype.hasOwnProperty.call(candidate, key),
    )
  ) {
    return false
  }

  return candidate.type === 'client_error'
}

function isIsoTimestamp(candidate: any): boolean {
  if (typeof candidate !== 'string') {
    return false
  }

  const isoRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|([+-]\d{2}:\d{2}))?$/
  return isoRegex.test(candidate) && !isNaN(Date.parse(candidate))
}

export function isMarkFaxAsReadResponse(
  candidate: any,
): candidate is MarkFaxAsReadResponse {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return false
  }

  const requiredKeys = ['message', 'read_at']
  if (
    requiredKeys.some(
      (key) => !Object.prototype.hasOwnProperty.call(candidate, key),
    )
  ) {
    return false
  }

  return (
    typeof candidate.message === 'string' && isIsoTimestamp(candidate.read_at)
  )
}

export default {
  create,
}
